import React, { useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { get } from "@/lib/api";
import { ErrorState, TableSkeleton } from "@/components/common/DataStates";
import { cn } from "@/lib/utils";
import { ActivitySection, ErrorsSections, RunsSection, UsageSections } from "./ObservabilitySections";

const tabs = [
  { label: "Usage IA", endpoint: "/admin/ai-usage?days=30", error: "Impossible de charger l’usage IA.", Content: UsageSections },
  { label: "Exécutions", endpoint: "/admin/runs?limit=100", error: "Impossible de charger les exécutions.", Content: RunsSection },
  { label: "Erreurs", endpoint: "/admin/errors?limit=50", error: "Impossible de charger les erreurs.", Content: ErrorsSections },
  { label: "Activité", endpoint: "/admin/activity?limit=100", error: "Impossible de charger l’activité.", Content: ActivitySection },
];

export default function PlatformObservabilityPage() {
  const [active, setActive] = useState(0);
  const tabRefs = useRef([]);
  const tab = tabs[active];
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ["platform-admin", "observability", tab.endpoint],
    queryFn: () => get(tab.endpoint),
    retry: false,
    staleTime: 60_000,
  });

  const onTabKeyDown = (event, index) => {
    let next;
    if (event.key === "ArrowRight") next = (index + 1) % tabs.length;
    else if (event.key === "ArrowLeft") next = (index + tabs.length - 1) % tabs.length;
    else if (event.key === "Home") next = 0;
    else if (event.key === "End") next = tabs.length - 1;
    else return;
    event.preventDefault();
    setActive(next);
    tabRefs.current[next]?.focus();
  };

  return (
    <div className="min-w-0 space-y-6" data-testid="platform-observability">
      <header>
        <p className="og-section-label">Administration · lecture seule</p>
        <h2 className="mt-1 font-heading text-2xl font-semibold">Observabilité</h2>
        <p className="mt-1 text-sm text-muted-foreground">Indicateurs opérationnels de la plateforme, sans contenu des organisations.</p>
      </header>
      <div role="tablist" aria-label="Rubriques d’observabilité" className="og-card grid min-w-0 grid-cols-2 gap-1 p-1.5 sm:flex">
        {tabs.map(({ label }, index) => <button
          key={label}
          type="button"
          role="tab"
          id={`observability-tab-${index}`}
          aria-controls="observability-panel"
          aria-selected={active === index}
          tabIndex={active === index ? 0 : -1}
          ref={(node) => { tabRefs.current[index] = node; }}
          onClick={() => setActive(index)}
          onKeyDown={(event) => onTabKeyDown(event, index)}
          className={cn(
            "og-focus min-w-0 rounded-md px-3 py-2 text-sm font-medium transition-colors",
            active === index ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-muted hover:text-foreground",
          )}
        >{label}</button>)}
      </div>
      <div id="observability-panel" role="tabpanel" aria-labelledby={`observability-tab-${active}`} tabIndex={0} className="min-w-0 og-focus">
        {isLoading ? <div className="og-card"><TableSkeleton testId="observability-loading" /></div>
          : isError ? <ErrorState message={tab.error} onRetry={refetch} />
            : <tab.Content data={data} />}
      </div>
    </div>
  );
}